import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AWS_URL } from "../../services/http.service";
import { getStorefrontPath } from "./storefrontPaths";

function ServiceCard({ product, theme, reviewStats }) {
  const navigate = useNavigate();
  const { bussinessUuid, businessName } = useParams();
  const isDarkTheme = (color) => {
    if (!color) return false;
    let hex = color.replace('#', '');
    if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  const darkMode = isDarkTheme(theme?.backgroundColor);

  const handleCardClick = () => {
    const serviceId = product.serviceId || product._id;
    navigate(
      getStorefrontPath({
        businessName,
        bussinessUuid,
        suffix: `booking/${serviceId}`,
      })
    );
  };

  return (
    <>
      <div
        className={`w-full rounded-xl shadow-md border overflow-hidden hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer ${
          darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
        }`}
        onClick={handleCardClick}
        style={{
          backgroundColor: darkMode ? undefined : theme?.backgroundColor,
          color: darkMode ? '#fff' : theme?.textColor,
        }}
      >
        {/* Product Content */}
        <div className='flex flex-col items-center px-3 pb-3 pt-2'>
          {/* Product Image Placeholder with subtle shine effect */}
          <div className='relative mb-2'>
            {product.serviceImage ? (
              <img
                className='w-32 h-32 object-cover rounded-xl shadow-md transition-transform duration-300 group-hover:scale-105'
                src={`${AWS_URL}${product.serviceImage}`}
                alt='serviceimage'
                style={{
                  borderRadius: '8px',
                  border: `1px solid ${theme?.primaryColor || '#4f46e5'}20`,
                }}
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <svg
                width='56'
                height='56'
                className={`text-gray-100 drop-shadow-sm ${darkMode ? 'opacity-20' : ''}`}
                fill='none'
                viewBox='0 0 64 64'
              >
                <polygon
                  points='32,12 56,52 8,52'
                  fill='currentColor'
                  className={darkMode ? 'text-blue-900' : 'text-blue-50'}
                />
                <polygon
                  points='32,20 44,44 20,44'
                  fill='currentColor'
                  className={darkMode ? 'text-blue-800' : 'text-blue-100'}
                />
              </svg>
            )}
          </div>

          {/* Product Info */}
          <div className='w-full text-center space-y-1'>
            <h3
              className={`font-bold text-sm leading-tight line-clamp-2 h-10 flex items-center justify-center ${
                darkMode ? 'text-white' : 'text-gray-800'
              }`}
              style={{ color: darkMode ? undefined : theme?.headingColor }}
            >
              {product.serviceName || "Unnamed Service"}
            </h3>
          </div>

          {/* Price and Add Button */}
          <div className='w-full mt-3 flex items-center justify-between'>
            <div className='flex items-baseline gap-2'>
              {product.discountAmount > 0 ? (
                <>
                  {/* Original Price (strikethrough) */}
                  <span
                    className={`text-sm line-through ${
                      darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}
                  >
                    ₹{product.salePrice}
                  </span>

                  {/* Discounted Price */}
                  <span
                    className={`font-bold text-base ${
                      darkMode ? 'text-white' : 'text-gray-900'
                    }`}
                    style={{ color: darkMode ? undefined : theme?.primaryColor }}
                  >
                    ₹{product.discountAmount}
                  </span>
                </>
              ) : (
                /* Only show salePrice if no discount */
                <span
                  className={`font-bold text-base ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                  style={{ color: darkMode ? undefined : theme?.primaryColor }}
                >
                  ₹{product.salePrice}
                </span>
              )}
            </div>

            <div className='flex items-baseline'>
              <span
                className={`font-bold text-base ${
                  darkMode ? 'text-gray-300' : 'text-gray-900'
                }`}
                style={{ color: darkMode ? undefined : theme?.textColor }}
              >
                {product.serviceDuration} mins
              </span>
            </div>
          </div>

          {/* Additional Info */}
          {Number(reviewStats?.totalReviews || 0) > 0 && (
            <div className='w-full mt-2 flex items-center justify-between text-[0.7rem]'>
              <span className={darkMode ? 'text-yellow-300' : 'text-amber-600'}>
                {'★'.repeat(Math.max(1, Math.round(Number(reviewStats?.averageRating || 0))))}
                {'☆'.repeat(Math.max(0, 5 - Math.round(Number(reviewStats?.averageRating || 0))))}
              </span>
              <span className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                {Number(reviewStats?.averageRating || 0).toFixed(1)} ({Number(reviewStats?.totalReviews || 0)} reviews)
              </span>
            </div>
          )}

          <div className='w-full mt-2 flex justify-between text-[0.65rem]'>
            <span
              className={`flex items-center ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-3 w-3 mr-0.5 text-green-500'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
              {product.openTime} opening time
            </span>

            <span
              className={`flex items-center ${
                darkMode ? 'text-gray-400' : 'text-gray-500'
              }`}
            >
              <svg
                xmlns='http://www.w3.org/2000/svg'
                className='h-3 w-3 mr-0.5 text-green-500'
                viewBox='0 0 20 20'
                fill='currentColor'
              >
                <path
                  fillRule='evenodd'
                  d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
                  clipRule='evenodd'
                />
              </svg>
              {product.closeTime} closing time
            </span>
          </div>

          {/* Subscription Options */}
          {product.subscriptionEligible && product.subscription && (
            <div
              className={`w-full mt-2 pt-2 border-t ${
                darkMode ? 'border-gray-700' : 'border-gray-100'
              }`}
            >
              <div className='text-[0.65rem] text-violet-600 font-medium flex items-center justify-center'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  className='h-3 w-3 mr-1'
                  viewBox='0 0 20 20'
                  fill='currentColor'
                >
                  <path d='M5 4a2 2 0 012-2h6a2 2 0 012 2v14l-5-2.5L5 18V4z' />
                </svg>
                Subscription Savings
              </div>
              <ul
                className={`mt-1 space-y-1 text-[0.6rem] ${
                  darkMode ? 'text-violet-400' : 'text-violet-500'
                }`}
              >
                {product.subscription.map((plan) => (
                  <li key={plan.frequency} className='flex justify-between'>
                    <span>{plan.frequency}:</span>
                    <span className='font-semibold'>
                      {plan.subscriptionDisc}
                      {plan.disUnit}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>





















































































    </>
  );
}

export default ServiceCard;